/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import LabourImpactSexTornado from './LabourImpactSexTornado';
import LabourImpactAgeStackedBar from './LabourImpactAgeStackedBar';

interface LabourImpactChartsProps {
  data: any[];
}

const LabourImpactCharts: FC<LabourImpactChartsProps> = ({ data }) => {
  const formatCellNumber = (val: number, hideK: boolean = false) => {
    if (val === 0) return '-';
    const num = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(val));
    return hideK ? num : num + ' k';
  };

  const sortedData = useMemo(() => {
    if (!data) return [];
    const filtered = data.filter((row) => {
      const ilo = row.ilo || {};
      return Object.values(ilo).some((v: any) => parseFloat(v.count || 0) > 0);
    });

    return [...filtered].sort((a, b) => {
      const getMagnitue = (row: any) => {
        const ilo = row.ilo || {};
        return Object.values(ilo).reduce((acc: number, v: any) => {
          return acc + Math.abs(parseFloat(v.count || 0)) + Math.abs(parseFloat(v.change || 0));
        }, 0);
      };
      return getMagnitue(b) - getMagnitue(a);
    }).slice(0, 10);
  }, [data]);

  const hasData = sortedData.length > 0;
  const firstRowIlo = hasData ? sortedData[0].ilo : {};
  const iloKeys = Object.keys(firstRowIlo);
  const isSexBased = iloKeys.includes('male') || iloKeys.includes('female');

  const sectorCodes = useMemo(() => sortedData.map((row) => row.oecd_code), [sortedData]);
  const sectorDescs = useMemo(() => sortedData.map((row) => row.desc), [sortedData]);
  const chartHeight = 600;

  if (!hasData) return null;

  return (
    <Row className="">
      <Col lg={12}>
        <Card className="shadow-none border">
          <Card.Header className="card-header-stretch">
            <Card.Title className="w-100 d-flex justify-content-between align-items-center">
              <div className='text-gray-800 fw-normal d-flex align-items-center justify-content-center'>
                <i className="fa-solid fa-chart-column me-5"></i>
                <div>
                  {isSexBased ? 'Top 10 Sectors: Male/Female Workforce Profile' : 'Top 10 Sectors: Age Group Workforce Profile'}
                </div>
              </div>
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="overflow-auto" style={{ maxHeight: '1000px' }}>
              <div className="p-4">
                {isSexBased ? (
                  <LabourImpactSexTornado 
                    data={sortedData} 
                    sectorCodes={sectorCodes} 
                    sectorDescs={sectorDescs} 
                    chartHeight={chartHeight} 
                    formatCellNumber={formatCellNumber} 
                  />
                ) : (
                  <LabourImpactAgeStackedBar 
                    data={sortedData} 
                    sectorCodes={sectorCodes} 
                    sectorDescs={sectorDescs} 
                    iloKeys={iloKeys} 
                    chartHeight={chartHeight} 
                    formatCellNumber={formatCellNumber} 
                  />
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default LabourImpactCharts;
